import { Button, Form, Input, InputNumber, Space } from "antd";
import { PopupTextSegmentDto } from "@shared/types/popup/popup.dto";

type PopupTextSegmentsEditorProps = {
  name: "nameStyles" | "descriptionStyles";
  label: string;
};

/** Normalize segment styles: drop empty keys; omit styles when all empty. */
export const normalizePopupTextSegments = (
  segments: PopupTextSegmentDto[] | null | undefined,
): PopupTextSegmentDto[] | null => {
  if (!segments?.length) {
    return null;
  }

  const normalized = segments
    .map((segment) => {
      const text = segment?.text ?? "";
      if (text.trim() === "") {
        return null;
      }

      const styles = segment.styles;
      if (!styles) {
        return { text };
      }

      const cleaned: PopupTextSegmentDto["styles"] = {};
      if (styles.fontFamily?.trim()) {
        cleaned.fontFamily = styles.fontFamily.trim();
      }
      if (styles.fontSize != null && !Number.isNaN(Number(styles.fontSize))) {
        cleaned.fontSize = Number(styles.fontSize);
      }
      if (
        styles.fontWeight != null &&
        String(styles.fontWeight).trim() !== ""
      ) {
        cleaned.fontWeight = styles.fontWeight;
      }
      if (styles.color?.trim()) {
        cleaned.color = styles.color.trim();
      }

      return Object.keys(cleaned).length
        ? { text, styles: cleaned }
        : { text };
    })
    .filter((segment): segment is PopupTextSegmentDto => segment != null);

  return normalized.length ? normalized : null;
};

/** Same as normalizePopupTextSegments but always returns an array (never null). */
export const normalizeDescriptionStyles = (
  segments: PopupTextSegmentDto[] | null | undefined,
): PopupTextSegmentDto[] => normalizePopupTextSegments(segments) ?? [];


const PopupTextSegmentsEditor = ({
  name,
  label,
}: PopupTextSegmentsEditorProps) => {
  return (
    <Form.Item label={label}>
      <Form.List name={name}>
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <Space
                key={field.key}
                direction="vertical"
                style={{
                  display: "flex",
                  marginBottom: 12,
                  padding: 12,
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  width: "100%",
                }}
              >
                <Form.Item
                  {...field}
                  name={[field.name, "text"]}
                  label="Text"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Segment text is required",
                    },
                  ]}
                  style={{ marginBottom: 8 }}
                >
                  <Input.TextArea
                    rows={2}
                    placeholder="Segment text (supports [x] / [ENTITY])"
                  />
                </Form.Item>

                <Space wrap style={{ width: "100%" }}>
                  <Form.Item
                    {...field}
                    name={[field.name, "styles", "fontFamily"]}
                    label="Font family"
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="e.g. Poppins-Bold" />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, "styles", "fontSize"]}
                    label="Font size"
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber min={1} placeholder="14" />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, "styles", "fontWeight"]}
                    label="Font weight"
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="e.g. 700" />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, "styles", "color"]}
                    label="Color"
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="#FFFFFF" />
                  </Form.Item>
                </Space>

                <Button danger type="link" onClick={() => remove(field.name)}>
                  Remove segment
                </Button>
              </Space>
            ))}

            <Button type="dashed" onClick={() => add({ text: "" })} block>
              Add segment
            </Button>
          </>
        )}
      </Form.List>
    </Form.Item>
  );
};

export default PopupTextSegmentsEditor;
